import { isNil } from "lodash";

import { ContinuousQuestionTypes } from "@/constants/questions";
import {
  ConditionalPost,
  GroupOfQuestionsPost,
  NotebookPost,
  Post,
  PostGroupOfQuestions,
  PostStatus,
  PostWithForecasts,
  QuestionPost,
} from "@/types/post";
import {
  Question,
  QuestionType,
  QuestionWithForecasts,
  QuestionWithMultipleChoiceForecasts,
  QuestionWithNumericForecasts,
  Scaling,
} from "@/types/question";

export function isMultipleChoicePost(
  post: PostWithForecasts
): post is QuestionPost<QuestionWithMultipleChoiceForecasts> {
  return post.question?.type === QuestionType.MultipleChoice;
}

/**
 * Canonical "is this question discrete?" check for behavioural logic (PMF/bar
 * rendering, bucket snapping). Mirrors Question.is_discrete() on the backend.
 *
 * New date questions are always binned, but legacy date questions have no
 * granularity and keep continuous behaviour so their bucket grid — and so their
 * resolutions and scores — are unchanged. Distinct from the "is it a date?"
 * check (QuestionType.Date) used for date formatting.
 */
export function isDiscreteQuestion(
  question: Pick<Question, "type" | "date_granularity">
): boolean {
  if (question.type === QuestionType.Discrete) return true;
  // Quantitative questions always carry a precision, so they are always binned.
  if (question.type === QuestionType.Quantitative) return true;
  if (question.type === QuestionType.Date) return !!question.date_granularity;
  return false;
}

/**
 * Where inside a bin its reportable value sits, as a fraction of the bin's
 * width. The two binning conventions differ here and must not be conflated —
 * getting this wrong shifts every tick label, resolution marker and admin
 * resolution input by half a bin.
 *
 * Discrete and Quantitative questions store `range_min` half a step BELOW their
 * displayed lower bound, so their reportable values land on bin centres.
 *
 * Binned date questions are half-open: bin `i` covers
 * `[range_min + i*step, range_min + (i+1)*step)` and reports its lower edge,
 * with `range_max` as the exclusive outer edge. So `range_max - range_min` is
 * exactly `binCount * step`.
 */
export const CENTRED_BIN_ATOM_OFFSET = 0.5;
export const HALF_OPEN_BIN_ATOM_OFFSET = 0;

export type BinScale = {
  binCount: number;
  atomOffset: number;
};

type BinScaleQuestion = Pick<Question, "type" | "date_granularity"> & {
  inbound_outcome_count?: number | null;
};

export function getBinAtomOffset(question: Pick<Question, "type">): number {
  return question.type === QuestionType.Discrete ||
    question.type === QuestionType.Quantitative
    ? CENTRED_BIN_ATOM_OFFSET
    : HALF_OPEN_BIN_ATOM_OFFSET;
}

/**
 * The bin grid of a discrete question, or null when the question is continuous
 * and so has no bins. Deliberately does not default the bin count: a question
 * carrying no `inbound_outcome_count` is treated as unbinned rather than
 * silently given 200 bins.
 */
export function getBinScale(
  question: BinScaleQuestion,
  fallbackBinCount?: number | null
): BinScale | null {
  if (!isDiscreteQuestion(question)) return null;

  const binCount = question.inbound_outcome_count ?? fallbackBinCount;
  if (isNil(binCount) || !Number.isFinite(binCount) || binCount <= 0) {
    return null;
  }

  return { binCount, atomOffset: getBinAtomOffset(question) };
}

/**
 * Unscaled ([0, 1]) location of the value bucket `bucketIndex` reports. Bucket 0
 * is strictly below the lower bound, 1..binCount are the inbound bins and
 * binCount + 1 is above the upper bound. Mirrors
 * bucket_index_to_unscaled_location in utils/the_math/formulas.py.
 *
 * The out-of-bound buckets fall out of the same formula: for half-open bins the
 * upper one lands on 1, which is range_max — the exclusive outer edge, and so
 * the first value above the range.
 */
export function getBucketAtom(
  bucketIndex: number,
  { binCount, atomOffset }: BinScale
): number {
  return (bucketIndex - 1 + atomOffset) / binCount;
}

/** The reportable value of every inbound bin, in unscaled [0, 1] space. */
export function getInboundBucketAtoms(binScale: BinScale): number[] {
  return Array.from({ length: binScale.binCount }, (_, i) =>
    getBucketAtom(i + 1, binScale)
  );
}

/**
 * The value reported by the bin CONTAINING `unscaled`, so a value anywhere in a
 * bin renders on that bin's bar.
 *
 * Values outside [0, 1] are returned unchanged: those are the out-of-bounds
 * buckets, which have their own sentinel atoms.
 */
export function snapToContainingBucketAtom(
  unscaled: number,
  binScale: BinScale
): number {
  if (!Number.isFinite(unscaled) || unscaled <= 0 || unscaled >= 1) {
    return unscaled;
  }
  return getBucketAtom(bucketIndexAt(unscaled, binScale) + 1, binScale);
}

/**
 * Unscaled ([0, 1)) edges of the bin CONTAINING `unscaled`, as
 * `[lower, upper)`. Null outside the range, where the bucket is one of the
 * out-of-bounds masses and has no finite interval.
 *
 * Note the bounds are NOT the ones snapToContainingBucketAtom uses: zero is
 * inbound here, being the first bin's lower edge, and both conventions put
 * their below-range atom strictly below it (-1/n half-open, -0.5/n centred).
 * The distinction only shows up on a question with a CLOSED lower bound, where
 * the chart pins the cursor to exactly zero — an inbound bin, which reading
 * zero as out-of-bounds would report as "below the range".
 *
 * The edges are the same for both binning conventions — only the atom the bin
 * reports differs — so a caller wanting to NAME the interval must still decide
 * whether that is meaningful: a centred bin IS its value, and reporting the
 * -0.5 to 0.5 around a count question's "0" would be worse, not more precise.
 */
export function getContainingBucketRange(
  unscaled: number,
  binScale: BinScale
): [number, number] | null {
  if (!Number.isFinite(unscaled) || unscaled < 0 || unscaled >= 1) {
    return null;
  }
  const index = bucketIndexAt(unscaled, binScale);
  return [index / binScale.binCount, (index + 1) / binScale.binCount];
}

/**
 * Zero-based index of the inbound bin containing `unscaled`, which the caller
 * has already established is inbound.
 *
 * Floors rather than taking the nearest atom. For centred bins the two agree,
 * since the atom is the bin's midpoint — but for half-open bins the atom is the
 * lower edge, so a value in the latter part of a bin is nearest the NEXT bin's
 * atom while belonging to this one. Flooring keeps this in step with
 * scaled_location_to_date_bucket_index, which decides the bin the value was
 * actually scored in.
 */
function bucketIndexAt(unscaled: number, binScale: BinScale): number {
  return Math.min(
    Math.floor(unscaled * binScale.binCount),
    binScale.binCount - 1
  );
}

export function isQuestionPost<QT>(post: Post<QT>): post is QuestionPost<QT> {
  return !isNil(post.question);
}

export function isGroupOfQuestionsPost<QT>(
  post: Post<QT>
): post is GroupOfQuestionsPost<
  QT extends Question | QuestionWithNumericForecasts ? QT : Question
> {
  return !isNil(post.group_of_questions);
}

export function isConditionalPost<QT>(
  post: Post<QT>
): post is ConditionalPost<
  QT extends Question | QuestionWithNumericForecasts ? QT : Question
> {
  return !isNil(post.conditional);
}

export function isNotebookPost(post: Post): post is NotebookPost {
  return !isNil(post.notebook);
}

export function getPostTitle(post: Post) {
  if (post.conditional) {
    return post.conditional.condition_child.title;
  }

  return post.title;
}

export function getQuestionStatus(post: PostWithForecasts | null) {
  const isLive = post?.status == PostStatus.OPEN;
  const isDone =
    post?.curation_status == PostStatus.RESOLVED ||
    post?.curation_status == PostStatus.CLOSED ||
    post?.curation_status == PostStatus.DELETED;
  const hasForecasts = post?.nr_forecasters ? post.nr_forecasters > 0 : false;

  return { isLive, isDone, hasForecasts };
}

export function parseQuestionId(
  questionUrlOrId: string,
  includeNotebooks: boolean = false
): {
  postId: number | null;
  questionId: number | null;
} {
  const result: {
    postId: number | null;
    questionId: number | null;
  } = {
    postId: null,
    questionId: null,
  };

  // Check for subquestion ID in query params
  const subQuestionPattern = /sub-question=(\d+)/;
  const subQuestionMatch = questionUrlOrId.match(subQuestionPattern);
  if (subQuestionMatch && subQuestionMatch[1]) {
    result.questionId = Number(subQuestionMatch[1]);
  }

  // Check for post ID
  const id = Number(questionUrlOrId);
  if (!isNaN(id)) {
    result.postId = id;
  } else {
    const urlPattern = includeNotebooks
      ? /\/(?:questions|notebooks)\/(\d+)\/?/
      : /\/questions\/(\d+)\/?/;
    const match = questionUrlOrId.match(urlPattern);
    if (match && match[1]) {
      result.postId = Number(match[1]);
    }
  }

  return result;
}

export function getPostDrivenTime(rawTime: string | null | undefined) {
  return rawTime ? new Date(rawTime).getTime() : undefined;
}

export function getContinuousGroupScaling(
  questions: QuestionWithNumericForecasts[]
) {
  const rangeMaxPoints: number[] = [];
  const rangeMinPoints: number[] = [];
  const zeroPoints: number[] = [];
  questions.forEach((question) => {
    if (question.scaling.range_max !== null) {
      rangeMaxPoints.push(question.scaling.range_max);
    }

    if (question.scaling.range_min !== null) {
      rangeMinPoints.push(question.scaling.range_min);
    }

    if (question.scaling.zero_point !== null) {
      zeroPoints.push(question.scaling.zero_point);
    }
  });
  const scaling: Scaling = {
    range_max: rangeMaxPoints.length > 0 ? Math.max(...rangeMaxPoints) : null,
    range_min: rangeMinPoints.length > 0 ? Math.min(...rangeMinPoints) : null,
    // set zero_point to null if any are linearly scaled
    zero_point:
      zeroPoints.length === questions.length ? Math.min(...zeroPoints) : null,
  };
  // if zero_point ends up lying within the range, remove the scaling
  if (
    !isNil(scaling.zero_point) &&
    !isNil(scaling.range_min) &&
    !isNil(scaling.range_max) &&
    scaling.range_min <= scaling.zero_point &&
    scaling.zero_point <= scaling.range_max
  ) {
    scaling.zero_point = null;
  }
  return scaling;
}

export function checkGroupOfQuestionsPostType<T extends QuestionType>(
  post: PostWithForecasts,
  type: T
): post is PostWithForecasts & {
  group_of_questions: PostGroupOfQuestions<QuestionWithForecasts & { type: T }>;
} {
  return (
    isGroupOfQuestionsPost(post) &&
    post.group_of_questions.questions[0]?.type === type
  );
}

export function isContinuousQuestionType(type: QuestionType): boolean {
  return ContinuousQuestionTypes.includes(
    type as (typeof ContinuousQuestionTypes)[number]
  );
}

/**
 * "Does this question report a number rather than a date?" — the choice between
 * numeric and date formatting, wording ("above" vs "after") and units.
 *
 * Distinct from isContinuousQuestionType, which also covers Date. Distinct again
 * from isDiscreteQuestion, which asks whether the outcome space is binned.
 */
export function isNumericQuestionType(type: QuestionType): boolean {
  return (
    type === QuestionType.Numeric ||
    type === QuestionType.Discrete ||
    type === QuestionType.Quantitative
  );
}

export function isContinuousQuestion(question: QuestionWithForecasts): boolean {
  return isContinuousQuestionType(question.type);
}

/**
 * Narrows to the numeric-forecast shape, which the inline
 * `type === Numeric || ... || Date` chains used to do by control flow — callers
 * need it to read `forecast_values` as `number[]`.
 *
 * Deliberately keyed off MultipleChoice rather than continuousness, so it is
 * sound in both directions: Binary questions also carry numeric forecasts, so a
 * guard written as `question is QuestionWithNumericForecasts` from
 * isContinuousQuestionType would let tsc conclude that a later
 * `type === Binary` branch is dead.
 */
export function hasNumericForecasts(
  question: QuestionWithForecasts
): question is QuestionWithNumericForecasts {
  return question.type !== QuestionType.MultipleChoice;
}

export function isValidScaling(
  scaling: Scaling | null | undefined
): scaling is {
  range_min: number;
  range_max: number;
  zero_point: number | null;
} {
  return (
    !isNil(scaling) && !isNil(scaling.range_min) && !isNil(scaling.range_max)
  );
}

/**
 * Returns the effective QuestionType for a post:
 * - Single question: the question's actual type
 * - Group of questions: an inferred group type (Numeric for fan graph, Date otherwise)
 * - Conditional: type of condition child
 * - Notebook: null
 */
export function inferEffectiveQuestionTypeFromPost(
  post: PostWithForecasts
): QuestionType | null {
  if (isQuestionPost(post)) return post.question.type;
  if (isGroupOfQuestionsPost(post))
    return post.group_of_questions.questions.at(0)?.type || null;
  if (isConditionalPost(post)) return post.conditional.condition_child.type;

  return null;
}

export function getAllOptionsHistory(question: Question): string[] {
  const allOptions: string[] = [];
  (question.options_history ?? []).map((entry) => {
    entry[1].slice(0, -1).forEach((option) => {
      if (!allOptions.includes(option)) {
        allOptions.push(option);
      }
    });
  });
  const other = (question.options ?? []).at(-1);
  if (other) {
    allOptions.push(other);
  }
  return allOptions;
}

export function getUpcomingOptions(question: Question): string[] {
  const optionsHistory = question.options_history;
  if (!optionsHistory || optionsHistory.length < 2) {
    return [];
  }
  const lastEntry = optionsHistory.at(-1);
  if (!lastEntry || new Date().getTime() > new Date(lastEntry[0]).getTime()) {
    return [];
  }
  const secondLastOptions = optionsHistory.at(-2)?.[1];
  return lastEntry[1].filter(
    (option) => !(secondLastOptions && secondLastOptions.includes(option))
  );
}
